import { Component, Vue, Prop } from 'vue-property-decorator'
import Loader from '@/components/Loader/index.vue'
import vSelect from 'vue-select'

import { library } from '@fortawesome/fontawesome-svg-core'
import {
	faClose,
	faLocationDot,
	faScaleBalanced,
	faBuilding,
	faChartPie,
	faChevronRight,
	faChevronLeft,
} from '@fortawesome/free-solid-svg-icons'

import api from '@/helpers/fetch'
import Fuse from 'fuse.js'

library.add(
	faClose,
	faLocationDot,
	faScaleBalanced,
	faBuilding,
	faChartPie,
	faChevronRight,
	faChevronLeft,
)

@Component({
	components: {
		Loader,
		vSelect,
	},
})
export default class AdvancedFiltersModal extends Vue {
	searchResult = [] as Array<any>

	form = {
		company_name: null,
		state: null,
		city: null,
		law_firm: null,
		market_sector: null,
		calc_subject: null,
		publicly_traded: null,
		multinational: null,
		distribution_date: null,
		distribution_date_formatted: null as string | null,
		research_date: new Date(),
	}

	options = {
		companies: [] as Array<any>,
		courthouses: [] as Array<any>,
		calcSubjects: [] as Array<any>,
		sectors: [] as Array<any>,
		firms: [] as Array<any>,
		states: [
			'SP',
			'RJ',
			'MG',
			'AC',
			'AL',
			'AP',
			'AM',
			'BA',
			'CE',
			'DF',
			'ES',
			'GO',
			'MA',
			'MT',
			'MS',
			'PA',
			'PB',
			'PR',
			'PE',
			'PI',
			'RN',
			'RS',
			'RO',
			'RR',
			'SC',
			'SE',
			'TO',
		],
	}

	initiatedSearch = false
	primarySearchField = ''
	loadingSearch = false
	loadingOptions = false
	itemsPerPage = 12
	currentPage = 0

	get selectedSearch() {
		return this.searchResult.filter((item) => item.selected === true)
	}

	get filteredSearch() {
		if (this.searchResult.length <= 0) return []

		const firstFilter = this.searchResult.filter((item) => {
			if (this.form.state && this.form.state !== item.state) {
				return false
			}

			if (
				this.form.calc_subject &&
				this.form.calc_subject !== item.calc_subject.id
			) {
				return false
			}

			if (this.form.law_firm && this.form.law_firm !== item.law_firm.id) {
				return false
			}

			if (
				this.form.market_sector &&
				this.form.market_sector !== item.market_sector.id
			) {
				return false
			}

			return true
		})

		const fuse = new Fuse(firstFilter, {
			keys: [
				// @ts-ignore
				{ name: 'city', getFn: (entry) => entry.city },
				// @ts-ignore
				{ name: 'company_name', getFn: (entry) => entry.company_name },
			],
			shouldSort: true,
			ignoreLocation: true,
			threshold: 0.3,
		})

		const query = {} as Record<string, any>
		if (this.form.city) {
			query.city = this.form.city
		}

		if (this.form.company_name) {
			query.company_name = this.form.company_name
		}

		if (this.form.city || this.form.company_name) {
			// @ts-ignore
			return fuse.search(query).map(({ item }) => item)
		} else {
			return firstFilter
		}
	}

	get pagedSearch() {
		const result = [] as Array<any>
		const firstIndex = this.itemsPerPage * this.currentPage
		let lastIndex = firstIndex + this.itemsPerPage
		if (lastIndex >= this.filteredSearch.length) {
			lastIndex = this.filteredSearch.length
		}

		for (let n = firstIndex; n < lastIndex; n++) {
			result.push(this.filteredSearch[n])
		}

		return result
	}

	get totalPages() {
		return Math.ceil(this.filteredSearch.length / this.itemsPerPage)
	}

	get selectedCompanies() {
		return new Set(
			this.selectedSearch.reduce((final, entry) => {
				if (entry.company_name) final.push(entry.company_name)
				return final
			}, []),
		)
	}

	nextPage() {
		if (this.currentPage + 1 >= this.totalPages) return
		this.currentPage++
	}

	prevPage() {
		if (this.currentPage - 1 < 0) return
		this.currentPage--
	}

	async loadCalcSubjects() {
		const res = await api.get('/api/calc/subjects?outdated=false')
		this.options.calcSubjects = []
		this.options.calcSubjects.push(...res.data)
	}

	async loadSectors() {
		const res = await api.get('/api/market-sectors')
		this.options.sectors = []
		this.options.sectors.push(...res.data)
	}

	async loadFirms() {
		const res = await api.get('/api/law-firms')
		this.options.firms = []
		this.options.firms.push(...res.data)
	}

	fuseSearch(options, search) {
		if (search.length === 0) return options

		const fuse = new Fuse(options, {
			keys: ['name'],
			shouldSort: true,
			ignoreLocation: true,
			threshold: 0.3,
		})

		return fuse.search(search).map(({ item }) => item)
	}

	toggleEntry(entry) {
		if (this.selectedCompanies.size >= 5) {
			this.$bvToast.toast(
				'Remova uma empresa ou resete sua pesquisa para prosseguir',
				{
					title: 'Limite de empresas atingido!',
					variant: 'warning',
					solid: true,
					toaster: 'b-toaster-top-center',
				},
			)
			return
		}
		entry.selected = !entry.selected
	}

	clearForm() {
		this.form.company_name = null
		this.form.state = null
		this.form.city = null
		this.form.law_firm = null
		this.form.market_sector = null
		this.form.calc_subject = null
	}

	reset() {
		this.clearForm()
		this.currentPage = 0
		this.primarySearchField = ''
		this.initiatedSearch = false
		this.loadingSearch = false
		this.loadingOptions = false
		this.searchResult = []
	}

	hidden() {
		this.reset()
		this.$emit('hidden')
	}

	async loadSearchBySubject() {
		const res = await api.get(
			'/api/thermometer/collections/by/subject?calc_subject_id=' +
				this.form.calc_subject,
		)

		if (res.ok) {
			this.searchResult = res.data.map((item) => {
				item.selected = false
				return item
			})
		}
	}

	async loadSearchByState() {
		const res = await api.get(
			'/api/thermometer/collections/by/state?state=' + this.form.state,
		)

		if (res.ok) {
			this.searchResult = res.data.map((item) => {
				item.selected = false
				return item
			})
		}
	}

	async loadSearchByLawFirm() {
		const res = await api.get(
			'/api/thermometer/collections/by/law-firm?law_firm_id=' +
				this.form.law_firm,
		)

		if (res.ok) {
			this.searchResult = res.data.map((item) => {
				item.selected = false
				return item
			})
		}
	}

	async load(fieldInitiator) {
		if (this.initiatedSearch) {
			if (fieldInitiator !== this.primarySearchField) return

			this.loadingSearch = true
			switch (fieldInitiator) {
				case 'subject':
					if (!this.form.calc_subject) {
						this.reset()
						return
					}

					await this.loadSearchBySubject()
					break
				case 'state':
					if (!this.form.state) {
						this.reset()
						return
					}

					await this.loadSearchByState()
					break
				case 'law_firm':
					if (!this.form.law_firm) {
						this.reset()
						return
					}

					await this.loadSearchByLawFirm()
					break
				default:
					console.info(`Campo inicial inesperado ${fieldInitiator}.`)
			}

			this.loadingSearch = false
			return
		}

		this.initiatedSearch = true
		this.loadingSearch = true
		if (this.form.calc_subject) {
			this.primarySearchField = 'subject'
			await this.loadSearchBySubject()
		} else if (this.form.state) {
			await this.loadSearchByState()
			this.primarySearchField = 'state'
		} else if (this.form.law_firm) {
			await this.loadSearchByLawFirm()
			this.primarySearchField = 'law_firm'
		}
		this.loadingSearch = false
	}

	done() {
		this.$emit('done', this.selectedSearch)
		this.$bvModal.hide('termometro-filtros-avancados')
	}

	async mounted() {
		this.loadingOptions = true
		await this.loadSectors()
		await this.loadCalcSubjects()
		await this.loadFirms()
		this.loadingOptions = false
	}
}
